import React from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'

// Selectors
import {
  selectRentalPropertyBudget,
  selectRentalPropertyDownPayment,
  selectRentalPropertyMonthlyPayment
} from '../../../../../store/selectors/home/equity'
import { selectRentalPropertyMortgageRate } from '../../../../../store/selectors/mortgageRate'
import { selectShowBuyersLink } from '../../../../../store/selectors/routes'
import { selectInvestmentVideoId, selectInvestmentVideoType } from '../../../../../store/selectors/videos'

// Constants
import { FROM } from '../../../../../mappings/navigation'
import { rentalProperty } from '../../../../../constants/equity'

// Components
import { Format, If, Text } from '../../../..'
import { Player } from '../../../../shared/Player'
import { Heading, ProConList, Pro, Info, Strong } from '../../../../shared/Gem/GemDetail'
import MonthlyPayment from '../../MonthlyPayment'
import { BuyersLink } from '../../BuyersLink'
import CTAHeading from '../../CTAHeading'
import NotEnoughEquity from './NotEnoughEquity'

// Styles
import styles from './RentalPropertyDetail.module.scss'
import { HOMEOWNER_DIGEST_PURCHASING_POWER_INVESTMENT_PROPERTY_PANEL } from '../../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

const { RENTAL_PROPERTY } = FROM

const RentalPropertyDetail = () => {
  const budget = useSelector(selectRentalPropertyBudget)
  const downPaymentAmount = useSelector(selectRentalPropertyDownPayment)
  const monthlyPayment = useSelector(selectRentalPropertyMonthlyPayment)
  const mortgageRate = useSelector(selectRentalPropertyMortgageRate)
  const showBuyersLink = useSelector(selectShowBuyersLink)

  const videoId = useSelector(selectInvestmentVideoId)
  const videoType = useSelector(selectInvestmentVideoType)

  if (downPaymentAmount < rentalProperty.minimumEquity) {
    return <NotEnoughEquity />
  }

  return (
    <div>
      <Heading>
        <FormattedMessage
          id='Homeowner.purchasePower.rentalPropertyHeading'
          defaultMessage='You could take around {downPaymentAmount} of your equity and use it as a {downPaymentPercent} down payment on an investment home worth {budget}.'
          values={{
            downPaymentAmount: (
              <Strong>
                <Format.Currency abbreviate value={downPaymentAmount} />
              </Strong>
            ),
            downPaymentPercent: (
              <Strong>
                <Format.Percentage value={rentalProperty.downPaymentPercent * 100} />
              </Strong>
            ),
            budget: (
              <Strong>
                <Format.Currency abbreviate value={budget} />
              </Strong>
            )
          }}
        />
      </Heading>
      <If condition={videoType && videoId}>
        <Player className={styles.video} videoType={videoType} videoId={videoId} />
      </If>
      <ProConList className={styles.proCon}>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentalPropertyOne'
            defaultMessage='Your new monthly principal and interest payment on a {loanTerm}-year mortgage at {apr} would be around {payment}'
            values={{
              apr: <Format.Rate value={mortgageRate} />,
              loanTerm: rentalProperty.loanTerm,
              payment: <MonthlyPayment value={monthlyPayment} />
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentalPropertyTwo'
            defaultMessage='Consult your tax advisor to see if expenses related to this property can be {taxDeductible}'
            values={{
              taxDeductible: (
                <Strong>
                  <FormattedMessage
                    id='Homeowner.purchasePower.rentalPropertyTaxDeductible'
                    defaultMessage='deducted'
                  />
                </Strong>
              )
            }}
          />
        </Pro>
        <Pro>
          <FormattedMessage
            id='Homeowner.purchasePower.rentalPropertyThree'
            defaultMessage='Use rental income to cover some or all of your new mortgage payment'
          />
        </Pro>
        <Info>
          <FormattedMessage
            id='Homeowner.purchasePower.rentalPropertyFour'
            defaultMessage='Consider if you have time to be a landlord (or if you should hire a property manager)'
          />
        </Info>
      </ProConList>

      <If condition={showBuyersLink}>
        <CTAHeading className={styles.cta}>
          <FormattedMessage
            id='Homeowner.purchasePower.rentalPropertyCTA'
            defaultMessage='Find the best markets for investments'
          />
        </CTAHeading>
        <Text align='center' variant='neutralCool' variantCount='900'>
          <BuyersLink
            affordability={budget}
            from={RENTAL_PROPERTY}
            tracking={{
              guid: 'tfBRKai88VYucaLMCaheen',
              ui_context: HOMEOWNER_DIGEST_PURCHASING_POWER_INVESTMENT_PROPERTY_PANEL
            }}
          />
        </Text>
      </If>
    </div>
  )
}

export default RentalPropertyDetail
